import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Props {
  /** Broker/agency view: the client being edited. Omit in the employer view (own client). */
  clientId?: number;
}

/**
 * Per-client toggle: whether employees see the premium-cost breakdown popover
 * on their benefit cards. Editable by broker (with clientId) and employer
 * (own client). Reads/writes /api/benefits/display-settings.
 */
export function BenefitsDisplaySettingsCard({ clientId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const url = clientId
    ? `/api/benefits/display-settings?clientId=${clientId}`
    : `/api/benefits/display-settings`;

  const { data, isLoading } = useQuery<{ showPremiumBreakdownToEmployees?: boolean }>({
    queryKey: ["/api/benefits/display-settings", clientId ?? "self"],
    queryFn: async () => {
      const res = await fetch(url);
      if (!res.ok) return { showPremiumBreakdownToEmployees: true };
      return res.json();
    },
  });

  const show = data?.showPremiumBreakdownToEmployees !== false;

  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiRequest("PUT", "/api/benefits/display-settings", {
        showPremiumBreakdownToEmployees: next,
        ...(clientId ? { clientId } : {}),
      });
      if (!res.ok) throw new Error("Failed to save setting");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/benefits/display-settings", clientId ?? "self"] });
      toast({ title: "Setting saved", variant: "success" });
    },
    onError: () => toast({ title: "Failed to save setting", variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Employee display</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="show-premium-breakdown">Show employees how their premium is calculated</Label>
            <p className="text-xs text-muted-foreground">
              When off, employees still see their own cost — just not the per-member / employer breakdown.
            </p>
          </div>
          <Switch
            id="show-premium-breakdown"
            checked={show}
            disabled={isLoading || mutation.isPending}
            onCheckedChange={(v) => mutation.mutate(v)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
